import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../api";
import { logoUrl } from "../../branding";
import { useSession } from "../../shell/session";
import { formatDateTime } from "../../../shared/time";
import type { PlanSummary, SongSummary } from "../../../shared/types";
import { listCachedPlans, listCachedSongs } from "./cache";
import { getOfflineReadiness } from "./download";
import { practiceFrom } from "./practice";
import "./music-stand-home.css";

export function MusicStandHomePage() {
  const { user, signOut, offlineSession } = useSession();
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [songs, setSongs] = useState<SongSummary[]>([]);
  const [tab, setTab] = useState<"sets" | "songs">("sets");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [offline, setOffline] = useState(false);
  const [savedPlanIds, setSavedPlanIds] = useState<Set<string>>(new Set());
  const [savedSongIds, setSavedSongIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const cachedPlans = await listCachedPlans().catch(() => []);
    const cachedSongs = await listCachedSongs().catch(() => []);
    const verifiedPlans = (await Promise.all(cachedPlans.map(async (plan) => ({ plan, ready: (await getOfflineReadiness(plan).catch(() => null))?.ready === true })))).filter((row) => row.ready).map((row) => row.plan);
    const verifiedSongs = (await Promise.all(cachedSongs.map(async (song) => ({ song, ready: (await getOfflineReadiness(practiceFrom(song)).catch(() => null))?.ready === true })))).filter((row) => row.ready).map((row) => row.song);
    setSavedPlanIds(new Set(verifiedPlans.map((plan) => plan.id)));
    setSavedSongIds(new Set(verifiedSongs.map((song) => song.id)));
    try {
      const [nextPlans, nextSongs] = await Promise.all([
        api<PlanSummary[]>("/api/v1/plans?status=active"),
        api<SongSummary[]>("/api/v1/songs?limit=100"),
      ]);
      setPlans(nextPlans);
      setSongs(nextSongs);
      setOffline(false);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.status === 401
          ? "Your sign-in expired. Connect and sign in again to open your music."
          : caught.message || "Unable to load your music.");
        setOffline(false);
        return;
      }
      setOffline(true);
      setPlans(verifiedPlans.filter((plan) => plan.serviceTypeName !== "Practice").map((plan) => ({ ...plan, itemCount: plan.items.length, pendingResponses: 0 })));
      setSongs(verifiedSongs);
      if (!verifiedPlans.length && !verifiedSongs.length) setError("No complete sets or songs are saved on this device. Connect to the internet and use Download set before performing offline.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "MB Music Stand";
    void load();
    return () => { document.title = "MB Live"; };
  }, [load]);

  const visiblePlans = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return plans.filter((plan) => plan.title.toLocaleLowerCase().includes(term) || plan.serviceTypeName.toLocaleLowerCase().includes(term));
  }, [plans, query]);
  const visibleSongs = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return songs.filter((song) => song.title.toLocaleLowerCase().includes(term) || song.authors.toLocaleLowerCase().includes(term));
  }, [songs, query]);
  const upcoming = visiblePlans.filter((plan) => Date.parse(plan.startsAt) >= Date.now() - 24 * 60 * 60 * 1000);
  const shownPlans = upcoming.length ? upcoming : visiblePlans;

  return <main className="music-app-home">
    <header className="music-app-header">
      <div className="music-app-brand"><img src={logoUrl} alt="" /><span><strong>MB Music Stand</strong><small>Moksha Base</small></span></div>
      <button type="button" className="music-app-signout" onClick={() => void signOut()}>Sign out</button>
    </header>

    <section className="music-app-intro">
      <p className="music-app-eyebrow">Ready for the stage</p>
      <h1>Your music, ready to play.</h1>
      <p>Open a service set or practise a song. Download a set while connected to keep its charts and files on this device.</p>
      <span className="music-app-greeting">{offline || offlineSession ? `Offline library on this device${user?.name ? ` · ${user.name}` : ""}` : `Signed in as ${user?.name ?? "your account"}`}</span>
    </section>
    {offline && <p className="music-app-offline" role="status">Offline mode · Only fully downloaded music is shown. Changes and annotations will sync when you reconnect.</p>}

    <div className="music-app-tabs" role="tablist" aria-label="Music library">
      <button type="button" role="tab" aria-selected={tab === "sets"} className={tab === "sets" ? "active" : ""} onClick={() => { setTab("sets"); setQuery(""); }}>Service sets</button>
      <button type="button" role="tab" aria-selected={tab === "songs"} className={tab === "songs" ? "active" : ""} onClick={() => { setTab("songs"); setQuery(""); }}>Songs</button>
    </div>
    <label className="music-app-search">
      <span>Find {tab === "sets" ? "a set" : "a song"}</span>
      <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={tab === "sets" ? "Search service sets" : "Search songs or writers"} />
    </label>

    {loading && <p className="music-app-state">Loading your music…</p>}
    {!loading && error && <div className="music-app-state" role="alert"><p>{error}</p><button type="button" onClick={() => void load()}>Try again</button></div>}
    {!loading && !error && tab === "sets" && <section className="music-app-list" aria-label="Service sets">
      {shownPlans.length === 0 && <p className="music-app-state">{offline ? "No downloaded sets match your search." : "No sets found. Create a service plan in MB Live, then open it here."}</p>}
      {shownPlans.map((plan) => <Link className="music-app-card" to={`/stand/${encodeURIComponent(plan.id)}`} key={plan.id}>
        <span className="music-app-card-icon" aria-hidden="true">♫</span>
        <span className="music-app-card-copy"><strong>{plan.title}</strong><small>{formatDateTime(plan.startsAt)} · {plan.itemCount} items</small><em>{plan.serviceTypeName}{savedPlanIds.has(plan.id) ? " · Saved offline" : ""}</em></span>
        <span className="music-app-card-arrow" aria-hidden="true">→</span>
      </Link>)}
    </section>}
    {!loading && !error && tab === "songs" && <section className="music-app-list" aria-label="Songs">
      {visibleSongs.length === 0 && <p className="music-app-state">{offline ? "No downloaded songs match your search." : "No songs found."}</p>}
      {visibleSongs.map((song) => <Link className="music-app-card" to={`/stand/${encodeURIComponent(song.id)}`} key={song.id}>
        <span className="music-app-card-icon" aria-hidden="true">♪</span>
        <span className="music-app-card-copy"><strong>{song.title}</strong><small>{song.authors || "Moksha Base"}</small><em>{song.defaultKey} {song.bpm ? `· ${song.bpm} BPM` : ""}{savedSongIds.has(song.id) ? " · Saved offline" : ""}</em></span>
        <span className="music-app-card-arrow" aria-hidden="true">→</span>
      </Link>)}
    </section>}
    <footer className="music-app-footer">Download your set and test opening it without a connection before a performance.</footer>
  </main>;
}
